'use client'
import cn from 'classnames'
import { FunctionComponent, useCallback } from 'react'
import { toast } from 'react-toastify'

import {
  acceptTermsValidator,
  phoneValidator,
  requiredFieldValidator,
} from '@/utils/validator'
import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm } from 'react-hook-form'
import { object as yupObject, string as yupString } from 'yup'

import { AcceptCheckbox } from '@/components/AcceptCheckbox'
import { Button } from '@/components/Button'
import { FormInput } from '@/components/FormInput'
import { HeaderTag } from '@/components/HeaderTag'
import { Loader } from '@/components/Loader'
import { TextArea } from '@/components/TextArea'

import { YANDEX_METRIKA, YANDEX_METRIKA_COUNTER } from '@/config'

import { HelpTypeSite } from '@/services/Api/generated'

import { getHelp } from '@/app/actions/help/actions'
import { marketingStorage } from '@/store/MarketingStorage'
import { useCaptcha } from '@/hooks/Captcha'

import { Logo } from './resources/logo'

import moduleStyles from '@/modules/HomePage/HomePage.module.scss'
import { maxPopupStore, maxRequestFormTexts } from '@/store/MaxPopup.store'
import styles from './Support.module.scss'

interface IFormData {
  name: string
  phone: string
  comment?: string
  accept: boolean
}

const schema = yupObject().shape({
  name: requiredFieldValidator,
  phone: phoneValidator,
  comment: yupString(),
  accept: acceptTermsValidator,
})

type SupportProps = {
  className?: string
}
export const Support: FunctionComponent<SupportProps> = ({ className }) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<IFormData>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })
  const { getToken } = useCaptcha()

  const onSubmit: SubmitHandler<IFormData> = useCallback(
    async (data) => {
      const tokenCaptcha = await getToken()
      const response = await getHelp(
        {
          type: HelpTypeSite.AskRequest,
          data: {
            sourceUrl: window.location.href.split('?')[0],
            phone: data.phone,
            name: data.name,
            comment: data.comment,
            utmClientId: marketingStorage.getClientId() ?? undefined,
          },
        },
        tokenCaptcha
      )
      if (response.status) {
        marketingStorage.clearClientId()
        if (YANDEX_METRIKA) {
          // @ts-ignore
          ym(YANDEX_METRIKA_COUNTER, 'reachGoal', 'FORM_SUPPORT_SUBMIT')
        }

        setValue('phone', '')
        setValue('name', '')
        setValue('accept', false)
        setValue('comment', '')

        maxPopupStore.show({
          sourceSite: response.data.params,
          ...maxRequestFormTexts,
        })
      } else {
        toast.error('Что то пошло не так! Попробуйте позже')
      }
    },
    [setValue, getToken]
  )

  return (
    <div className={cn(className, moduleStyles.container)}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.dot}></div>
          <div className={styles.dot}></div>
          <div className={styles.dot}></div>
        </div>
        <div className={styles.backgroundContainer}>
          <img
            src="/images/dots.svg"
            className={styles.backgroundDots}
            alt=""
          />
        </div>
        <div className={styles.body}>
          <HeaderTag
            icon="cog"
            title="Техническая поддержка"
            className={moduleStyles.tag}
          />
          <div className={styles.title}>Помогаем разобраться в деталях</div>
          <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            <Logo className={styles.logo} />

            <div className={styles.formTitle}>Задайте нам вопрос</div>

            <div className={styles.row}>
              <FormInput
                className={styles.input}
                placeholder="Ваше имя"
                error={errors.name?.message}
                {...register('name')}
              />
              <FormInput
                className={styles.input}
                mask="+{7} (000) 000-00-00"
                placeholder="+7 (999) 999-99-99"
                error={errors.phone?.message}
                {...register('phone')}
              />
            </div>
            <TextArea
              placeholder="Напишите Ваш вопрос"
              className={styles.input}
              error={errors.comment?.message}
              {...register('comment')}
            />
            <Button
              type="submit"
              className={styles.button}
              disabled={isSubmitting}
              compactHover
            >
              Задать вопрос
              {isSubmitting ? <Loader onTop /> : null}
            </Button>
            <AcceptCheckbox<IFormData>
              className={styles.checkbox}
              register={register}
              error={errors.accept?.message}
            />
          </form>
        </div>
      </div>
    </div>
  )
}
